import * as React from "react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import {
  Button,
  Currency,
  FlexRow,
  H3,
  Link,
  RichText,
  Text,
  AppError,
  IconShare,
  CopyToClipboard,
  IconTwitter,
  IconConfetti,
  FadeIn,
  IconX,
  CurrencyIcon,
  IconFire,
} from "atoms";
import { useAuth } from "features/auth";
import { useLocation } from "react-router-dom";
import { GenericImage } from "features/images";
import { trackEvent } from "contexts/mixpanel";
import { formatDate, timeSinceDate } from "utils/date";
import { useUpdatePost } from "../api/usePosts";
import { isOtherPost, isSalePost } from "../utils/postTypes";
import { usePostReact } from "../api/usePostReact";
import type { Post } from "../types";

const trackSignIn = () =>
  trackEvent("Button Click", {
    ButtonName: "Sign In",
    ButtonContext: "Post Card",
    Location: window.location.href,
  });
const trackSignUp = () =>
  trackEvent("Button Click", {
    ButtonName: "Sign Up",
    ButtonContext: "Post Card",
    Location: window.location.href,
  });

type WithPost = { post: Post };
type PostCardProps = WithPost & {
  vertical?: boolean;
  artistHeader?: React.ReactNode;
  isPermalink?: boolean;
};

const PostCard = React.memo(
  ({
    post,
    artistHeader,
    isPermalink,
    vertical = true,
    ...props
  }: PostCardProps) => {
    // Card img should be square, cropped, and centered when in the grid
    const imgAspectRatio = isPermalink ? "auto" : "1";
    // Determining which size to show also depends on if we're in the grid
    const imgSizes = isPermalink
      ? "(max-width: 300px) 300px, (max-width: 600px) 600px, 1200px"
      : "(max-width: 300px) 300px, (max-width: 1325px) 600px, 300px";

    return (
      <PostCardWrapper
        flexDirection={vertical ? "column" : "row"}
        alignItems="flex-start"
        flexWrap="nowrap"
        {...props}
      >
        {artistHeader ?? null}

        <PostMain>
          {post.image && (
            <PostImage
              src={post.image}
              aspectRatio={imgAspectRatio}
              sizes={imgSizes}
            />
          )}

          <PostTag post={post} />

          <PostTitle>
            {isPermalink ? (
              post.title
            ) : (
              <Link
                to={`/artists/${post.destinations[0].feedId}/updates/${post.id}`}
              >
                {post.title}
              </Link>
            )}
          </PostTitle>

          <PostMeta post={post} />

          <PostContent value={post.text} isPermalink={isPermalink || false} />
        </PostMain>

        <PostLink post={post} />

        <DateAndAuthor>
          <PostedBy post={post} />
          <PostedAt post={post} />
        </DateAndAuthor>

        <PostCardMenu post={post} />
      </PostCardWrapper>
    );
  }
);

const PostCardWrapper = styled(FlexRow)(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  gap: 10,
  border: "1px solid",
  borderColor: theme.colors.gray10,
  boxShadow: theme.boxShadow.light,
  borderRadius: theme.borderRadius.sm,
  fontSize: 14,
  maxHeight: 700,
  width: "100%",
  maxWidth: 400,
  margin: "0 auto",
  padding: "4% 4% 0 4%",
  "&>*": {
    margin: 0,
  },
}));

const PostImage = styled(GenericImage)({
  flex: "0 0 auto",
});

const PostMain = styled.div({
  "&&": {
    flex: "1 1 auto",
  },
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  overflow: "hidden",
  width: "100%",
});

const PostTitle = styled(H3)(({ theme }) => ({
  fontSize: theme.fontSizes.md,
  lineHeight: 1.1,
  textTransform: "none",
  "&&": { margin: 0 },
  a: {
    textDecoration: "none",
  },
  "a:hover, a:focus-visible": {
    color: theme.colors.fg,
  },
  "a::after": {
    content: "''",
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 2,
  },
}));

type PostContentProps = {
  value: string;
  isPermalink: boolean;
};
function PostContent({ value, isPermalink = false }: PostContentProps) {
  return <PostRichText value={value} isPermalink={isPermalink} />;
}
const PostRichText = styled(RichText)<{ isPermalink: boolean }>(
  ({ isPermalink, theme }) => ({
    color: theme.colors.fg,
    flex: 1,
    fontSize: theme.fontSizes.xs,
    lineHeight: 1.4,
    overflow: "hidden",
    position: "relative",
    "p, &>*": {
      ...theme.fonts.body,
    },
    "ul,ol": {
      paddingLeft: "1.5em",
    },
    "p:last-of-type": {
      margin: 0,
    },
    "&, & *": {
      fontSize: isPermalink ? 15 : 13.5,
    },
    ...(!isPermalink && {
      p: {
        display: "inline",
        paddingRight: ".5ch",
      },
      "p:has(br:only-child)": {
        display: "none",
      },
      // ".ql-editor": {
      //   overflow: "hidden",
      //   lineClamp: "3",
      //   WebkitLineClamp: "3",
      //   WebkitBoxOrient: "vertical",
      //   textOverflow: "ellipsis",
      //   display: "-webkit-box",
      //   width: "100%",
      // },
      "&::after": {
        content: "''",
        display: "block",
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "2em",
        backgroundImage: `linear-gradient(to top, white 0%, rgb(0 0 0 / 0) 100%)`,
      },
    }),
  })
);

function PostTag({ post }: WithPost) {
  if (isOtherPost(post) && !post.deletedAt) return null;

  let tagText = `${post.postType[0]}${post.postType.slice(1).toLowerCase()}`;

  if (isSalePost(post)) {
    tagText = `${
      post.saleTags.filter((tag) => tag !== "Manifold" && tag !== "New Drop")[0]
    } Drop`;
  }

  return (
    <FlexRow gap="10px">
      {!isOtherPost(post) && (
        <StyledPostTag isSale={isSalePost(post)}>{tagText}</StyledPostTag>
      )}
      {post.isTrending && (
        <TrendingTag>
          Trending&nbsp;
          <IconFire />
        </TrendingTag>
      )}
      {post.deletedAt && <DeletedTag>Deleted</DeletedTag>}
    </FlexRow>
  );
}

const StyledPostTag = styled.div<{ isSale?: boolean }>(({ theme, isSale }) => ({
  ...theme.fonts.display,
  backgroundColor: isSale ? theme.colors.successL : theme.colors.accent2LL,
  textTransform: "uppercase",
  borderRadius: theme.borderRadius.sm,
  fontSize: 12,
  letterSpacing: "0.02em",
  lineHeight: 1,
  color: theme.colors.fg,
  display: "inline-block",
  padding: ".5em 1em",
}));

const TrendingTag = styled(StyledPostTag)(({ theme }) => ({
  backgroundColor: theme.colors.accent4LL,
  svg: {
    transform: "scale(1.3)",
    transformOrigin: "left",
  },
}));

const DeletedTag = styled(StyledPostTag)(({ theme }) => ({
  backgroundColor: theme.colors.errorL,
  svg: {
    transform: "scale(1.3)",
    transformOrigin: "left",
  },
}));

function PostMeta({ post }: WithPost) {
  if (!isSalePost(post)) return null;

  const isManifold = post.saleTags.includes("Manifold");

  const isFreeManifold =
    isManifold && (!post.salePrice || post.salePrice === "0");

  if (!post.saleStart && !post.saleEnd && !isFreeManifold && !post.salePrice)
    return null;

  return (
    <MetaText>
      <FlexRow alignItems="flex-start" gap="3px 1em">
        {(post.saleStart || post.saleEnd) && (
          <span>
            {post.saleStart && (
              <span>
                {!post.saleEnd && <>Available&nbsp;</>}
                {post.saleStart &&
                  formatDate(post.saleStart, {
                    month: "short",
                    day: "numeric",
                  })}
              </span>
            )}

            {post.saleEnd && (
              <>
                {post.saleStart ? (
                  <>&#8201;&ndash;&#8201;</>
                ) : (
                  <>Available&nbsp;until&nbsp;</>
                )}

                {formatDate(post.saleEnd, {
                  month: "short",
                  day: "numeric",
                })}
              </>
            )}
          </span>
        )}

        {isFreeManifold ? (
          <Free>
            Free{" "}
            <CurrencyIcon
              unit={
                post.saleCurrency !== undefined ? post.saleCurrency : "null"
              }
            />
          </Free>
        ) : (
          <span>
            <Currency value={post.salePrice} unit={post.saleCurrency} />
          </span>
        )}
      </FlexRow>
    </MetaText>
  );
}

const MetaText = styled.div(({ theme }) => ({
  color: theme.colors.fg,
  fontSize: 15,
  lineHeight: 1,
  paddingBottom: 3,
  position: "relative",
  zIndex: 4,
}));

const Free = styled.div({
  alignItems: "center",
  display: "inline-flex",
  svg: {
    marginLeft: 2,
    marginTop: -1,
    maxWidth: 15,
    height: 15,
    width: "auto",
  },
});

const trackClick = (ButtonContext: string, ButtonDestination: string) => () =>
  trackEvent("Button Click", {
    ButtonName: "Post Link",
    ButtonContext,
    ButtonDestination,
    Location: window.location.href,
  });

function PostLink({ post }: WithPost) {
  let buttonText = "See more";
  let screenReaderText = `Read more about at ${post.link}`;

  if (isSalePost(post)) {
    if (post.soldOut) {
      buttonText = "View Now (Sold Out)";
      screenReaderText = `View ${post.title} by ${post.destinations[0].feedId} (Sold Out)`;
    } else if (!!post.saleEnd && new Date(post.saleEnd) < new Date()) {
      buttonText = "View Now (Sale Ended)";
      screenReaderText = `View ${post.title} by ${post.destinations[0].feedId} (Sale Ended)`;
    } else {
      buttonText = "Buy Now";
      screenReaderText = `Buy ${post.title} by ${post.destinations[0].feedId} Now`;
    }
  }

  if (!post.link) return null;

  return (
    <FlexRow
      alignItems="center"
      gap="10px"
      style={{ flex: "0 0 auto", marginTop: 10 }}
    >
      <Link
        variant="secondary"
        size="xxs"
        href={post.link}
        aria-label={screenReaderText}
        target="_blank"
        onClick={trackClick(buttonText, post.link)}
      >
        {buttonText}
      </Link>
    </FlexRow>
  );
}

const DateAndAuthor = styled(Text)({
  color: "#959595",
  margin: 0,
  fontStyle: "italic",
  fontSize: 13,
  lineHeight: 1.15,
});

function PostedBy({ post }: WithPost) {
  if (post.postedByHug) return <>Curated by HUG</>;
  if (post.automatedPost) return <>Posted via Manifold</>;
  return <>Posted by {post.artistName}</>;
}

function PostedAt({ post }: WithPost) {
  if (!post.createdAt) return null;

  const postedAtDate = timeSinceDate(new Date(post.createdAt));

  return (
    <>
      {" "}
      <time
        dateTime={post.createdAt.toString()}
        style={{ whiteSpace: "nowrap" }}
      >
        {postedAtDate}
      </time>
    </>
  );
}

function PostCardMenu({ post }: WithPost) {
  const { user } = useAuth();

  const location = useLocation();

  const { isSuccess, isError, error, data } = useUpdatePost(post.id);

  // handles post card footer menu
  const [activeMenu, setActiveMenu] = React.useState("");
  const resetMenus = () => setActiveMenu("");
  const createActiveHandler = (menu: string) => () =>
    activeMenu === menu ? resetMenus() : setActiveMenu(menu);

  const { cheer, unCheer } = usePostReact(post.id);

  const { artistName, artistTwitter } = post;

  function extractTwitterHandle(url: string) {
    if (!url) return null;
    const match = url.match(
      /^https?:\/\/(www\.)?twitter.com\/@?(?<handle>\w+)/
    );
    return match?.groups?.handle ? `@${match.groups.handle}` : null;
  }
  const twitterHandle = !artistTwitter
    ? null
    : extractTwitterHandle(artistTwitter);

  const postUrl = `https://thehug.xyz/artists/${post.destinations[0].feedId}/updates/${post.id}`;

  let tweetText = `Check out this ${
    post.postType === "OTHER" ? "update" : post.postType.toLocaleLowerCase()
  } from ${
    twitterHandle || artistName
  }. %0a%0aLearn more on HUG: https://thehug.xyz/artists/${
    post.destinations[0].feedId
  }/updates/${post.id} %0a%0a@thehugxyz`;

  if (post.postType === "SALE") {
    tweetText = `Check out ${twitterHandle || artistName}'s ${
      post.saleTags.filter((tag) => tag !== "Manifold" && tag !== "New Drop")[0]
    } Drop, "${
      post.title
    }"! %0a%0aLearn more on HUG:  https://thehug.xyz/artists/${
      post.destinations[0].feedId
    }/updates/${post.id} %0a%0a@thehugxyz`;
  }
  const tweetUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;

  const { cheerCount } = post;
  const myCheerStatus = post.myReactions?.[0] === "CHEER";
  const toggleCheer = myCheerStatus ? unCheer : cheer;

  return (
    <>
      {isSuccess && (
        <Text size="xxxs" textAlign="center">
          Deleted {formatDate(data.deletedAt)}
        </Text>
      )}
      <PostFooter>
        <PostButton
          onClick={createActiveHandler("share")}
          aria-expanded={activeMenu === "share" && true}
        >
          <IconShare size="1.1em" /> Share
        </PostButton>

        <LikeButton
          onClick={user ? toggleCheer : createActiveHandler("cheer")}
          userLiked={myCheerStatus}
        >
          <IconConfetti size="1.35em" />{" "}
          {!cheerCount ? "Cheer" : cheerCount.toString()}
        </LikeButton>

        {/* menus */}
        {activeMenu === "share" && (
          <PostMenu aria-live="polite">
            <PostMenuClose onClick={createActiveHandler("")}>
              <IconX aria-label="close share menu" />
            </PostMenuClose>
            <Text bold textAlign="center">
              Share this update
            </Text>
            <FlexRow justifyContent="center" itemsFlex="0 0 auto" gap="10px">
              <SCopyToClipboard buttonText="Copy link" value={postUrl} />
              <Link
                variant="primary"
                href={tweetUrl}
                target="_blank"
                rel="noreferrer"
              >
                <IconTwitter />
                Tweet
              </Link>
            </FlexRow>
          </PostMenu>
        )}

        {activeMenu === "cheer" && (
          <PostMenu aria-live="polite">
            <PostMenuClose onClick={createActiveHandler("")}>
              <IconX aria-label="close share menu" />
            </PostMenuClose>
            <Text bold textAlign="center">
              Sign in to cheer
            </Text>
            <FlexRow justifyContent="center" itemsFlex="0 0 auto" gap="10px">
              <Link
                variant="primary"
                size="xxs"
                to={`/register?artistId=${post.destinations[0].feedId}&postId=${post.id}`}
                state={{ from: location }}
                onClick={trackSignUp}
              >
                Create Account
              </Link>
              <Link
                variant="primary"
                size="xxs"
                to="/sign-in"
                state={{ from: location }}
                onClick={trackSignIn}
              >
                Sign in
              </Link>
            </FlexRow>
          </PostMenu>
        )}
      </PostFooter>

      {isError && (
        <AppError>
          {error instanceof Error ? error.message : "Couldn't delete update"}
        </AppError>
      )}
    </>
  );
}
const PostFooter = styled.div(({ theme }) => ({
  alignItems: "center",
  borderTop: "1px solid",
  borderColor: theme.colors.fg10,
  display: "flex",
  flexWrap: "nowrap",
  justifyContent: "space-around",
  gap: 10,
  padding: "10px 0",
  position: "relative",
  zIndex: 3,
  width: "100%",
  "&>*": {
    flex: "1 0 30%",
  },
  "&>*:only-child": {
    justifySelf: "flex-start",
  },
}));

const PostMenu = styled(FadeIn)(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  border: "1px solid",
  borderColor: theme.colors.fg60,
  borderRadius: theme.borderRadius.sm,
  bottom: "calc(100% - 1px)",
  boxShadow: theme.boxShadow.light,
  padding: "2rem .5rem",
  position: "absolute",
  left: "-3%",
  right: "-3%",
}));

const PostButton = styled(Button)(({ theme }) => ({
  fontWeight: 400,
  textTransform: "none",
  width: "100%",
  gap: 5,
  paddingLeft: 2,
  paddingRight: 2,
  alignItems: "center",
  backgroundColor: "inherit",
  border: "2px solid",
  borderColor: theme.colors.transparent,
  minHeight: 36,
  "&&": { color: theme.colors.fg70 },
  '&:hover, &:focus-visible, &[aria-expanded="true"]': {
    backgroundColor: theme.colors.accent3LL,
    borderColor: theme.colors.accent3LL,
    color: theme.colors.fg,
  },
  svg: {
    maxWidth: "none",
    maxHeight: "none",
    transform: "translateY(-9%)",
    position: "relative",
    top: -1,
  },
}));

const PostMenuClose = styled(Button)(({ theme }) => ({
  height: 30,
  width: 30,
  borderRadius: theme.borderRadius.sm,
  overflow: "hidden",
  position: "absolute",
  right: 0,
  top: 0,
  fontSize: 12,
  opacity: 0.4,
  "&:hover, &:focus": {
    border: 0,
    opacity: 1,
  },
}));
PostMenuClose.defaultProps = {
  variant: "blank",
};

PostButton.defaultProps = {
  size: "xxs",
  variant: "default",
};

const SCopyToClipboard = styled(CopyToClipboard)(({ theme }) => ({
  backgroundColor: theme.colors.fg,
  color: theme.colors.bg,
  border: "2px solid",
  borderColor: theme.colors.fg,
  borderRadius: theme.borderRadius.round,
  padding: "0.45em 1em 0.4em",
  lineHeight: 1.2,
  letterSpacing: ".02em",
  "&:hover, &:focus-visible": {
    backgroundColor: theme.colors.bg,
    color: theme.colors.fg,
  },
}));

const LikeButton = styled(PostButton, {
  shouldForwardProp: (prop) => prop !== "userLiked",
})<{ userLiked: boolean }>(({ theme, userLiked }) => ({
  ...(userLiked && {
    fontWeight: "bold",
    border: "2px solid",
    borderColor: theme.colors.accent3LL,
    svg: {
      backfaceVisibility: "hidden",
      animation: `${cheer} .7s .15s forwards`,
      transform: "scale(1) rotate(0deg)",
    },
    "&:hover, &:focus-visible": {
      borderColor: theme.colors.accent3,
    },
  }),
}));

const cheer = keyframes({
  "0%, 100%": {
    transform: "scale(1) rotate(0deg)",
    color: "inherit",
  },
  "20%": {
    transform: "scale(1.5) rotate(-15deg)",
    color: "#262626",
  },
  "40%": {
    transform: "scale(1.5) rotate(18deg)",
  },
  "60%": {
    transform: "scale(1.5) rotate(-10deg)",
  },
});

export { PostCard };
